import { User, Trip, Companion, TripParticipant } from '../types';
import { generateMockId, generateVoucherCode, formatMockDate } from '../utils/mockHelper';

/**
 * Dados mock para demonstração completa do sistema
 */

// Usuário demo principal
export const mockUser: User = {
  id: 1,
  name: 'Filipe Celedônio',
  email: 'tourist@jeridigital.com',
  phone: '85987654321',
  country_code: '+55',
  document_number: '123.456.789-10',
  date_of_birth: '1985-03-15',
  created_at: '2024-01-15T10:00:00Z',
  updated_at: '2025-08-19T10:00:00Z'
};

// Acompanhantes variados para demonstração
export const mockCompanions: Companion[] = [
  {
    id: 1,
    name: 'Maria Silva',
    document_number: '987.654.321-00',
    date_of_birth: '1988-07-22',
    email: 'maria.silva@email.com',
    phone: '85987654322',
    country_code: '+55',
    age: 37,
    created_at: '2024-02-10T14:30:00Z',
    updated_at: '2024-02-10T14:30:00Z'
  },
  {
    id: 2,
    name: 'Pedro Silva',
    document_number: '456.789.123-45',
    date_of_birth: '2016-12-05',
    email: null,
    phone: null,
    country_code: '+55',
    age: 8,
    created_at: '2024-02-10T14:35:00Z',
    updated_at: '2024-02-10T14:35:00Z'
  },
  {
    id: 3,
    name: 'Ana Costa',
    document_number: '789.123.456-78',
    date_of_birth: '1990-11-18',
    email: 'ana.costa@email.com',
    phone: '85987654323',
    country_code: '+55',
    age: 34,
    created_at: '2024-03-20T16:45:00Z',
    updated_at: '2024-03-20T16:45:00Z'
  },
  {
    id: 4,
    name: 'Carlos Oliveira',
    document_number: '321.654.987-12',
    date_of_birth: '1982-09-30',
    email: 'carlos.oliveira@email.com',
    phone: '85987654324',
    country_code: '+55',
    age: 42,
    created_at: '2024-04-15T11:20:00Z',
    updated_at: '2024-04-15T11:20:00Z'
  }
];

// Participantes para as viagens
const createParticipants = (companions: Companion[], isExempt: boolean[] = []): TripParticipant[] => {
  return companions.map((companion, index) => ({
    id: generateMockId(),
    trip_id: 0, // Será definido pela viagem
    name: companion.name,
    document_number: companion.document_number,
    date_of_birth: companion.date_of_birth,
    email: companion.email,
    phone: companion.phone,
    country_code: companion.country_code || '+55',
    individual_amount: isExempt[index] ? 0 : (companion.age && companion.age < 12 ? 15.00 : 30.00),
    is_exempt: isExempt[index] || false,
    exemption_reason_id: isExempt[index] ? 1 : null,
    voucher_code: generateVoucherCode(),
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  }));
};

// Viagens mock diversificadas
export const mockTrips: Trip[] = [
  {
    id: 1,
    user_id: 1,
    hotel_name: 'Vila Kalango',
    start_date: '2025-08-15',
    end_date: '2025-08-22',
    total_days: 7,
    total_amount: 75.00,
    payment_status_id: 2, // Pago
    voucher_code: 'JER2025VK001',
    voucher_expires_at: '2025-08-30T23:59:59Z',
    created_at: '2025-08-05T10:00:00Z',
    updated_at: '2025-08-10T15:30:00Z',
    participants: createParticipants([
      mockUser as any,
      mockCompanions[0],
      mockCompanions[1]
    ], [false, false, true]) // Pedro é isento (menor de 12 anos)
  },
  {
    id: 2,
    user_id: 1,
    hotel_name: 'Casa na Árvore',
    start_date: '2025-12-20',
    end_date: '2025-12-27',
    total_days: 7,
    total_amount: 120.00,
    payment_status_id: 1, // Pendente
    voucher_code: 'JER2025CA002',
    voucher_expires_at: '2026-01-05T23:59:59Z',
    created_at: '2025-08-01T14:20:00Z',
    updated_at: '2025-08-01T14:20:00Z',
    participants: createParticipants([
      mockUser as any,
      mockCompanions[0],
      mockCompanions[2],
      mockCompanions[3]
    ])
  },
  {
    id: 3,
    user_id: 1,
    hotel_name: 'Pousada do Vento',
    start_date: '2024-07-10',
    end_date: '2024-07-17',
    total_days: 7,
    total_amount: 60.00,
    payment_status_id: 2, // Pago
    voucher_code: 'JER2024PV003',
    voucher_expires_at: '2024-07-31T23:59:59Z',
    created_at: '2024-06-20T16:45:00Z',
    updated_at: '2024-07-05T09:15:00Z',
    participants: createParticipants([
      mockUser as any,
      mockCompanions[0]
    ])
  },
  {
    id: 4,
    user_id: 1,
    hotel_name: 'Essência do Mar',
    start_date: '2024-03-15',
    end_date: '2024-03-20',
    total_days: 5,
    total_amount: 150.00,
    payment_status_id: 2, // Pago
    voucher_code: 'JER2024EM004',
    voucher_expires_at: '2024-04-05T23:59:59Z',
    created_at: '2024-02-28T13:30:00Z',
    updated_at: '2024-03-10T11:45:00Z',
    participants: createParticipants([
      mockUser as any,
      mockCompanions[0],
      mockCompanions[1],
      mockCompanions[2]
    ], [false, false, true, false])
  },
  {
    id: 5,
    user_id: 1,
    hotel_name: 'Rancho do Peixe',
    start_date: '2024-12-28',
    end_date: '2025-01-05',
    total_days: 8,
    total_amount: 90.00,
    payment_status_id: 2, // Pago
    voucher_code: 'JER2024RP005',
    voucher_expires_at: '2025-01-15T23:59:59Z',
    created_at: '2024-12-10T09:15:00Z',
    updated_at: '2024-12-20T10:30:00Z',
    participants: createParticipants([
      mockUser as any,
      mockCompanions[0],
      mockCompanions[3]
    ])
  },
  {
    id: 6,
    user_id: 1,
    hotel_name: 'Hotel Hurricane',
    start_date: '2026-02-14',
    end_date: '2026-02-21',
    total_days: 7,
    total_amount: 60.00,
    payment_status_id: 1, // Pendente
    voucher_code: 'JER2026HH006',
    voucher_expires_at: '2026-03-05T23:59:59Z',
    created_at: '2025-08-18T12:00:00Z',
    updated_at: '2025-08-18T12:00:00Z',
    participants: createParticipants([
      mockUser as any,
      mockCompanions[0]
    ])
  }
];

// Atualizar trip_id nos participantes
mockTrips.forEach(trip => {
  trip.participants.forEach(participant => {
    participant.trip_id = trip.id;
  });
});

/**
 * Funções utilitárias para manipular dados mock
 */
export const getMockUserByEmail = (email: string): User | null => {
  return email === mockUser.email ? mockUser : null;
};

export const getMockTrips = (): Trip[] => {
  return [...mockTrips];
};

export const getMockCompanions = (): Companion[] => {
  return [...mockCompanions];
};

export const getMockTripById = (id: number): Trip | null => {
  return mockTrips.find(trip => trip.id === id) || null;
};

export const addMockTrip = (tripData: Partial<Trip>): Trip => {
  const newTrip: Trip = {
    id: generateMockId(),
    user_id: mockUser.id,
    hotel_name: tripData.hotel_name || '',
    start_date: tripData.start_date || formatMockDate(new Date()),
    end_date: tripData.end_date || formatMockDate(new Date()),
    total_days: tripData.total_days || 1,
    total_amount: tripData.total_amount || 0,
    payment_status_id: tripData.payment_status_id || 1, // 1 = Pendente por padrão
    voucher_code: generateVoucherCode(),
    voucher_expires_at: undefined,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    participants: tripData.participants || []
  };

  mockTrips.push(newTrip);
  return newTrip;
};

export const addMockCompanion = (companionData: Partial<Companion>): Companion => {
  const newCompanion: Companion = {
    id: generateMockId(),
    name: companionData.name || '',
    document_number: companionData.document_number || '',
    date_of_birth: companionData.date_of_birth || '',
    email: companionData.email || null,
    phone: companionData.phone || null,
    country_code: companionData.country_code || '+55',
    age: companionData.age || 0,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };

  mockCompanions.push(newCompanion);
  return newCompanion;
};

export const updateMockCompanion = (id: number, companionData: Partial<Companion>): Companion | null => {
  const index = mockCompanions.findIndex(c => c.id === id);
  if (index === -1) return null;

  mockCompanions[index] = {
    ...mockCompanions[index],
    ...companionData,
    updated_at: new Date().toISOString()
  };

  return mockCompanions[index];
};

export const deleteMockCompanion = (id: number): boolean => {
  const index = mockCompanions.findIndex(c => c.id === id);
  if (index === -1) return false;

  mockCompanions.splice(index, 1);
  return true;
};
